// ttyd WebSocket client
// ttyd uses a binary protocol with message type prefix

export type TtydEventHandler<T> = (data: T) => void;

interface TtydEventHandlers {
  output: TtydEventHandler<string>[];
  open: TtydEventHandler<void>[];
  close: TtydEventHandler<void>[];
}

// ttyd message types (first byte of binary message)
const MSG_INPUT = 0;      // Client -> Server: terminal input
const MSG_OUTPUT = 1;     // Server -> Client: terminal output
const MSG_TITLE = 2;      // Server -> Client: set window title
const MSG_PREFS = 3;      // Server -> Client: set preferences
const MSG_RECONNECT = 4;  // Server -> Client: reconnect timeout
const MSG_RESIZE = 5;     // Client -> Server: resize terminal
const MSG_PAUSE = 6;      // Server -> Client: pause output
const MSG_RESUME = 7;     // Server -> Client: resume output

export class TtydWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private textDecoder = new TextDecoder();
  private textEncoder = new TextEncoder();
  private handlers: TtydEventHandlers = {
    output: [],
    open: [],
    close: [],
  };

  constructor(url: string) {
    this.url = url;
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.shouldReconnect = true;
    this.ws = new WebSocket(this.url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      console.log('[ttyd] Connected');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.emit('open', undefined);
    };

    this.ws.onclose = () => {
      console.log('[ttyd] Disconnected');
      this.emit('close', undefined);

      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('[ttyd] Error:', error);
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data as ArrayBuffer);
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // Send terminal input
  sendInput(data: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const encoded = this.textEncoder.encode(data);
      const msg = new Uint8Array(1 + encoded.length);
      msg[0] = MSG_INPUT;
      msg.set(encoded, 1);
      this.ws.send(msg);
    }
  }

  // Send terminal resize
  sendResize(cols: number, rows: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const sizeStr = `${cols}:${rows}`;
      const encoded = this.textEncoder.encode(sizeStr);
      const msg = new Uint8Array(1 + encoded.length);
      msg[0] = MSG_RESIZE;
      msg.set(encoded, 1);
      this.ws.send(msg);
    }
  }

  on<K extends keyof TtydEventHandlers>(
    event: K,
    handler: TtydEventHandlers[K][number]
  ): () => void {
    (this.handlers[event] as unknown[]).push(handler);
    return () => {
      const handlers = this.handlers[event] as unknown[];
      const idx = handlers.indexOf(handler);
      if (idx >= 0) {
        handlers.splice(idx, 1);
      }
    };
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private handleMessage(data: ArrayBuffer): void {
    const bytes = new Uint8Array(data);
    if (bytes.length === 0) return;

    const msgType = bytes[0];
    const payload = bytes.slice(1);

    switch (msgType) {
      case MSG_OUTPUT:
        // Terminal output
        const text = this.textDecoder.decode(payload);
        this.emit('output', text);
        break;

      case MSG_TITLE:
        // Window title (ignore for now)
        break;

      case MSG_PREFS:
        // Preferences (ignore for now)
        break;

      case MSG_RECONNECT:
        // Reconnect timeout (ignore for now)
        break;

      case MSG_PAUSE:
      case MSG_RESUME:
        // Flow control (ignore for now)
        break;

      default:
        console.warn('[ttyd] Unknown message type:', msgType);
    }
  }

  private emit<K extends keyof TtydEventHandlers>(
    event: K,
    data: Parameters<TtydEventHandlers[K][number]>[0]
  ): void {
    for (const handler of this.handlers[event]) {
      try {
        (handler as (d: typeof data) => void)(data);
      } catch (e) {
        console.error(`[ttyd] Handler error for ${event}:`, e);
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`[ttyd] Reconnecting in ${Math.round(delay)}ms...`);

    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }
}
